import { ApiHelper } from 'app/api/api-helper'
import { ApiRequester } from 'app/api/api-requestor'
import {
    FetchSubscriptionPricingResponse,
    CreateCheckoutSessionResponse,
    ValidatePaymentResponse,
    CancelSubscriptionResponse,
    SubscriptionPricing,
} from 'app/api/types'
import { logger } from 'app/logging/Logger'

/**
 * Fetch subscription pricing information
 *
 * @returns Subscription pricing data with annual and monthly prices
 */
export async function fetchSubscriptionPricing(): Promise<SubscriptionPricing> {
    const operation = ApiHelper.buildFetchSubscriptionPricingRestOperation()
    const response =
        await ApiRequester.callGetOperationRequest<FetchSubscriptionPricingResponse>(
            operation
        )

    return response.prices
}

/**
 * Create a Stripe checkout session
 *
 * @param userId - User ID
 * @param priceId - Stripe price ID
 * @returns Checkout session URL
 */
export const createCheckoutSession = async (
    userId: string,
    priceId: string
): Promise<string> => {
    const operation = ApiHelper.buildCreateCheckoutSessionRestOperation(
        userId,
        priceId
    )
    const response =
        await ApiRequester.callGetOperationRequest<CreateCheckoutSessionResponse>(
            operation
        )

    logger.debug('Checkout session created', {
        action: 'create_checkout_session',
        userId,
        metadata: { priceId, sessionUrl: response.sessionUrl },
    })

    return response.sessionUrl
}

/**
 * Retrieve and validate checkout session success
 *
 * @param sessionId - Stripe session ID
 * @returns True if profile was upgraded successfully
 */
export const retrieveCheckoutSessionSuccess = async (
    sessionId: string
): Promise<boolean> => {
    const operation = ApiHelper.buildPaymentSuccessRestOperation(sessionId)
    const response =
        await ApiRequester.callGetOperationRequest<ValidatePaymentResponse>(
            operation
        )

    return response.profileUpgraded
}

/**
 * Cancel user subscription
 *
 * @param userId - User ID
 * @param reason - Cancellation reason
 * @returns True if cancellation was successful
 */
export async function cancelSubscription(
    userId: string,
    reason: string
): Promise<boolean> {
    const operation = ApiHelper.buildCancelSubscriptionRestOperation(
        userId,
        reason
    )
    const response =
        await ApiRequester.callGetOperationRequest<CancelSubscriptionResponse>(
            operation
        )

    return response.success
}
